// Timezone utilities. All timestamps are stored in UTC; we render them in the
// organization's selected IANA timezone. DST is handled automatically by Intl.

export const APP_VERSION = "pilot-1.0.0";
export const DEFAULT_TZ = "America/Chicago";

// Curated common IANA timezones with friendly region labels.
export const TIMEZONES = [
  { tz: "America/New_York", label: "Eastern Time" },
  { tz: "America/Chicago", label: "Central Time" },
  { tz: "America/Denver", label: "Mountain Time" },
  { tz: "America/Phoenix", label: "Mountain Time (no DST)" },
  { tz: "America/Los_Angeles", label: "Pacific Time" },
  { tz: "America/Anchorage", label: "Alaska Time" },
  { tz: "Pacific/Honolulu", label: "Hawaii–Aleutian Time" },
  { tz: "America/Toronto", label: "Eastern Time (Canada)" },
  { tz: "America/Vancouver", label: "Pacific Time (Canada)" },
  { tz: "America/Mexico_City", label: "Central Time (Mexico)" },
  { tz: "America/Sao_Paulo", label: "Brasília Time" },
  { tz: "Europe/London", label: "United Kingdom" },
  { tz: "Europe/Dublin", label: "Ireland" },
  { tz: "Europe/Paris", label: "Central European Time" },
  { tz: "Europe/Berlin", label: "Central European Time" },
  { tz: "Europe/Madrid", label: "Central European Time" },
  { tz: "Europe/Athens", label: "Eastern European Time" },
  { tz: "Europe/Moscow", label: "Moscow Time" },
  { tz: "Asia/Jerusalem", label: "Israel Time" },
  { tz: "Asia/Dubai", label: "Gulf Standard Time" },
  { tz: "Asia/Kolkata", label: "India Standard Time" },
  { tz: "Asia/Karachi", label: "Pakistan Time" },
  { tz: "Asia/Bangkok", label: "Indochina Time" },
  { tz: "Asia/Singapore", label: "Singapore Time" },
  { tz: "Asia/Hong_Kong", label: "Hong Kong Time" },
  { tz: "Asia/Shanghai", label: "China Standard Time" },
  { tz: "Asia/Tokyo", label: "Japan Standard Time" },
  { tz: "Asia/Seoul", label: "Korea Standard Time" },
  { tz: "Australia/Perth", label: "Australian Western Time" },
  { tz: "Australia/Sydney", label: "Australian Eastern Time" },
  { tz: "Pacific/Auckland", label: "New Zealand Time" },
  { tz: "UTC", label: "Coordinated Universal Time" },
];

export function offsetMinutes(tz, date = new Date()) {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const p = {};
    for (const part of dtf.formatToParts(date)) p[part.type] = part.value;
    const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return Math.round((asUTC - date.getTime()) / 60000);
  } catch { return 0; }
}

export function offsetLabel(tz, date = new Date()) {
  const m = offsetMinutes(tz, date);
  const sign = m >= 0 ? "+" : "-";
  const a = Math.abs(m);
  const hh = String(Math.floor(a / 60)).padStart(2, "0");
  const mm = String(a % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
}

export function tzMeta(tz) {
  const meta = TIMEZONES.find((t) => t.tz === tz);
  return { tz, label: meta ? meta.label : tz };
}

// "Central Time — America/Chicago"
export function tzDisplay(tz) {
  const meta = TIMEZONES.find((t) => t.tz === tz);
  return meta ? `${meta.label} — ${tz}` : tz;
}

// "All times shown in Central Time — America/Chicago"
export function tzBanner(tz) {
  return `All times shown in ${tzDisplay(tz)}`;
}

export function detectBrowserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ; }
  catch { return DEFAULT_TZ; }
}

function safeFmt(iso, tz, opts) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { timeZone: tz || DEFAULT_TZ, ...(opts || {}) });
  } catch { return "—"; }
}

export function fmtDateTime(iso, tz) {
  return safeFmt(iso, tz, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
export function fmtDateTimeFull(iso, tz) {
  return safeFmt(iso, tz, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
export function fmtDate(iso, tz, opts) {
  return safeFmt(iso, tz, opts || { month: "short", day: "numeric" });
}
export function fmtTime(iso, tz) {
  return safeFmt(iso, tz, { hour: "2-digit", minute: "2-digit" });
}

// Convert a wall-clock string ("YYYY-MM-DDTHH:mm" from a datetime-local input),
// interpreted as being in `tz`, into a UTC ISO string for storage.
export function zonedWallToUtcIso(wall, tz) {
  if (!wall) return "";
  const asUtc = new Date(wall.length === 16 ? wall + ":00Z" : wall + "Z");
  const off = offsetMinutes(tz || DEFAULT_TZ, asUtc);
  return new Date(asUtc.getTime() - off * 60000).toISOString();
}

// Convert a UTC ISO string into a wall-clock string ("YYYY-MM-DDTHH:mm") in `tz`
// for use as a datetime-local input value.
export function utcIsoToZonedWall(iso, tz) {
  const d = iso ? new Date(iso) : new Date();
  try {
    const dtf = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || DEFAULT_TZ, hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
    const p = {};
    for (const part of dtf.formatToParts(d)) p[part.type] = part.value;
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  } catch { return ""; }
}

export function nowZonedWall(tz) {
  return utcIsoToZonedWall(new Date().toISOString(), tz);
}

// "YYYY-MM-DD" calendar date of a UTC instant, in the given timezone.
export function zonedDateKey(iso, tz) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || DEFAULT_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date(iso));
  } catch { return ""; }
}

export function todayKey(tz) {
  return zonedDateKey(new Date().toISOString(), tz);
}
